import React, { useState } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import toast from "react-hot-toast";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import withAuth from "../../services/withAuthProvider";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { unitSelector } from "../../redux/unit.slice";
import NumberFormat from "react-number-format";
import MultiSelect from "../MultiSelect/MultiSelect";
import { useBalancedData } from "../../hooks/useStaleWhileRevalidate";

export default function ProxyBid(props) {
  const { push } = useHistory();
  const unit = useSelector(unitSelector);

  const handleClick = (path) => {
    push(path);
  };

  // Cache 5 menit untuk NIPL list - konsisten dengan cache strategy aplikasi
  const { data: dataNIPL = [], isLoading: isLoadingNIPL } = useBalancedData(
    ["proxyBidNIPL", unit?.current_auction_id],
    async () => {
      const response = await withAuth.getDataWithAuth(`/api/nipl/proxy-bid/${unit?.current_auction_id}`, {});
      if (response?.status === 200) {
        return response.data.data.data;
      }
      throw new Error(response?.data?.message || "Failed to fetch NIPL");
    },
    {
      cacheTime: 1000 * 60 * 5, // 5 menit cache
      staleTime: 0, // Always refetch in background
      enabled: !!unit?.current_auction_id,
      onError: (err) => {
        toast.error(err.message, {
          id: "error-message",
          duration: 3000,
        });
      },
    }
  );

  function isSpecialNIPL() {
    return unit?.number_of_nipl > 1;
  }

  // form validation rules
  const validationSchema = Yup.object().shape({
    nipl_buyer_id: Yup.string().required("Silahkan memilih NIPL"),
    offer_price: Yup.string()
      .required("Silahkan mengisi harga tawar")
      .test(
        "min-price",
        `Harga tawar tidak boleh kurang dari Rp.${Number(unit?.base_price).toLocaleString()}`,
        function (value) {
          if (!value) return false;
          const numericValue = Number(value.split(",").join(""));
          return numericValue >= (unit?.base_price || 0);
        }
      )
      .test(
        "multiple-bid",
        `Harga tawar harus kelipatan Rp.${Number(unit?.multiple_bid).toLocaleString()}`,
        function (value) {
          if (!value) return false;
          const numericValue = Number(value.split(",").join(""));
          const multipleBid = unit?.multiple_bid || 1;
          return numericValue % multipleBid === 0;
        }
      ),
  });
  const validationSpecialNiplSchema = Yup.object().shape({
    nipl_buyer_id: Yup.array()
      .nullable()
      .required("Silahkan memilih NIPL")
      .min(unit?.number_of_nipl || 1, `Minimal pembelian ${unit?.number_of_nipl || 1} NIPL`),
    offer_price: Yup.string()
      .required("Silahkan mengisi harga tawar")
      .test(
        "min-price",
        `Harga tawar tidak boleh kurang dari Rp.${Number(unit?.base_price).toLocaleString()}`,
        function (value) {
          if (!value) return false;
          const numericValue = Number(value.split(",").join(""));
          return numericValue >= (unit?.base_price || 0);
        }
      )
      .test(
        "multiple-bid",
        `Harga tawar harus kelipatan Rp.${Number(unit?.multiple_bid).toLocaleString()}`,
        function (value) {
          if (!value) return false;
          const numericValue = Number(value.split(",").join(""));
          const multipleBid = unit?.multiple_bid || 1;
          return numericValue % multipleBid === 0;
        }
      ),
  });

  // functions to build form returned by useForm() hook
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(isSpecialNIPL() ? validationSpecialNiplSchema : validationSchema),
  });

  const onSubmit = async (data, e) => {
    e.preventDefault();
    let dataProxyBid = null;
    const offer_price = data.offer_price.split(",").join("");
    if (isSpecialNIPL()) {
      dataProxyBid = {
        stock_unit_id: unit?.id,
        nipl_buyer_id: data?.nipl_buyer_id.map((nipl) => nipl?.value),
        offer_price: offer_price,
      };
    } else {
      dataProxyBid = new FormData();
      dataProxyBid.append("stock_unit_id", unit.id);
      dataProxyBid.append("nipl_buyer_id", data.nipl_buyer_id);
      dataProxyBid.append("offer_price", offer_price);
    }

    const loading = toast.loading("Mengirimkan...");

    const response = await withAuth.postDataWithAuth(
      isSpecialNIPL() ? `/api/proxy-bid/add-special` : "/api/proxy-bid/add",
      dataProxyBid
    );
    if (response?.status === 200) {
      toast.dismiss(loading);
      toast.success(response.data.message, {
        id: "success-message",
        duration: 3000,
      });
      props.handleClose();
    } else {
      toast.dismiss(loading);
      toast.error(response?.data?.errors?.details || response?.data?.message, {
        id: "error-message",
        duration: 3000,
      });
    }
    e.target.reset();
  };

  return (
    <>
      <div className="form-modal">
        <Form onSubmit={handleSubmit(onSubmit)}>
          {isSpecialNIPL() ? (
            <>
              <Controller
                control={control}
                name="nipl_buyer_id"
                render={({ field: { onChange, name, value = [] } }) => (
                  <>
                    <MultiSelect
                      placeholder="Pilih NIPL"
                      name={name}
                      noOptionsMessage="Tidak ada NIPL"
                      maxLimit={unit?.number_of_nipl}
                      minLimit={unit?.number_of_nipl}
                      onChange={onChange}
                      options={dataNIPL.map((data) => ({
                        label: data?.nipl_code + " " + data?.nipl,
                        value: data?.id,
                        data: data,
                      }))}
                      isOptionDisabled
                      value={value}
                    />
                    {errors.nipl_buyer_id?.message && (
                      <>
                        <span className="error-message">{errors.nipl_buyer_id?.message}</span>
                        <br />
                      </>
                    )}
                    <Form.Text className="text-muted">
                      Belum punya NIPL?{" "}
                      <span className="text-primary cursor-pointer" onClick={() => handleClick("/beli-nipl")}>
                        Beli NIPL
                      </span>
                    </Form.Text>
                  </>
                )}
              />
            </>
          ) : (
            <Form.Group className="form-group" controlId="formBasicEmail">
              <Form.Label className="required">NIPL</Form.Label>
              <Form.Select placeholder="Pilih NIPL" {...register("nipl_buyer_id")}>
                <option value="">Pilih NIPL</option>
                {dataNIPL.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.nipl_code + " " + row.nipl}
                  </option>
                ))}
              </Form.Select>
              {errors.nipl_buyer_id?.message && (
                <>
                  <span className="error-message">{errors.nipl_buyer_id?.message}</span>
                  <br />
                </>
              )}
              <Form.Text className="text-muted">
                Belum punya NIPL?{" "}
                <span className="text-primary cursor-pointer" onClick={() => handleClick("/beli-nipl")}>
                  Beli NIPL
                </span>
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="form-group" controlId="formBasicPassword">
            <Form.Label className="required">Harga Tawar</Form.Label>
            <InputGroup>
              <InputGroup.Text id="basic-addon1">Rp</InputGroup.Text>
              <Controller
                control={control}
                name="offer_price"
                render={({ field: { onChange, name, value } }) => (
                  <NumberFormat
                    className="form-control"
                    thousandSeparator={true}
                    name={name}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </InputGroup>
            {errors.offer_price?.message && (
              <>
                <span className="error-message">{errors.offer_price?.message}</span>
                <br />
              </>
            )}
            <Form.Text className="text-muted">Kelipatan bid Rp.{Number(unit?.multiple_bid).toLocaleString()}</Form.Text>
          </Form.Group>

          <div className="group">
            <Button className="cancel" variant="default" type="reset" onClick={props.handleClose}>
              Batal
            </Button>
            <Button variant="primary" type="submit">
              Tambahkan
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
}
